import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IAlertMessage } from 'src/app/core/models/models';
import { IReRouteModalQuote } from 'src/app/core/models/re-route.model';
import { ServicesIntegration } from 'src/app/integrations/models/services-integration.enum';
import { InvoluntaryChangeService } from 'src/app/involuntary-change/services/involuntary-change.service';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { ReRouteService } from 'src/app/re-route/services/re-route.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { ReportLogService } from 'src/app/shared/services/report-log.service';
import { ItineraryService } from '../../services/itinerary.service';
import {
  OptionType,
  MENU_ACTION_TYPES,
  ExceptionProcessType,
  ActionTypeText,
  TICKET_STATUS,
  WARNING_TEXT,
  DEVHOST
} from 'src/app/core/enums/enums';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  PassengerQuote,
  PnrAvailableOption,
  PnrInfo,
  QuoteRequest,
  WaiverData
} from 'src/app/core/models/itinerary.model';

@Component({
  selector: 'app-itinerary-actions',
  templateUrl: './itinerary-actions.component.html',
  styleUrls: ['./itinerary-actions.component.scss']
})
export class ItineraryActionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() ticketOpen = false;
  @Input() pnr: PnrInfo;
  @Input() noItinerary = false;
  @Input() hasPendingChanges = false;
  @Input() somePassengerHasTicket = false;
  @Input() exceptionMode: boolean;
  @Input() loadingActions: boolean;
  @Input() isReRoute: boolean;
  @Input() selectedSegments: number[];
  @Input() currentProcess$: ServicesIntegration;
  @Input() selectedPassengersQuote: PassengerQuote[];
  @Input() set actions(value: PnrAvailableOption) {
    this.action = value;
    if (value) {
      this.navigateToAction.emit(this.actionType);
    }
  }

  @Output() optionRequest = new EventEmitter<QuoteRequest>();
  @Output() changeActiveAction = new EventEmitter<string>();
  @Output() navigateToAction = new EventEmitter<string>();

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: any) {
    this.itineraryService.setReloadedPageSessionStorage();
  }
  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(event: any) {
    this.reportLog.FinishByUser(ActionTypeText.NA);
    return false;
  }

  ACTION_TYPES: typeof MENU_ACTION_TYPES = []; /* Don't change */
  isSelectedInvoluntary: boolean = false;
  action: PnrAvailableOption;
  selectedPassengersHasInvalidCoupons = false;
  isModalOpen = false;
  existAffectation = false;
  reRouteDisabled = true;
  buttonDisabled = true;
  isSalesForcesAirline = false;
  isAirline = false;
  isSalesForce = false;
  actionForm: FormGroup;
  actionType = '';
  waiver: WaiverData;
  tooltipMessage = 'itinerary-actions.select-option';
  pnrIsEncrypted = false;
  isVoluntaryChangeAllowed = true;
  showReRouteModal = false;
  showCanVoluntaryChange = false;
  showAlertsModal = false;
  alertMessages: IAlertMessage[] = [];
  receivedFromHasValue: boolean = false;
  isDevelop: boolean = false;
  reportLogActionTypeMap = {
    cancellation: 'Cancellation',
    specialRequest: 'SpeicalRequest',
    voluntaryChanges: 'Change',
    exchangeNoCost: 'Reschedule',
    openTicket: 'TicketOpening'
  };

  private subscriptions = new Subscription();

  get showInfantWarning(): boolean {
    let value = false;
    this.subscriptions.add(
      this.layoutService
        .getAlertMessages()
        .pipe(
          tap((alerts) => {
            value = alerts
              .map((alert) => alert.message)
              .includes('layout.alert-messages.infant-reassignment');
          })
        )
        .subscribe()
    );
    return value;
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private icService: InvoluntaryChangeService,
    private reportLog: ReportLogService,
    private layoutService: LayoutService,
    private reRouteService: ReRouteService,
    private settingsService: SettingsService,
    private itineraryService: ItineraryService
  ) {
    if (this.itineraryService.getReloadedPageSessionStorage()) {
      this.goToDashboard();
    }
  }

  ngOnInit() {
    this.isDevelop = window.location.host.includes(DEVHOST.host);
    this.callComponentSubcriptions();
    this.setterMenuActionsType();
    this.icService.loadAlternativesTrigger.subscribe(this.loadAlternativesRequest.bind(this));
    this.buildForm();
    this.ValidateIsAirline();
    if (!this.currentProcess$) {
      this.existAffectation = true;
      this.checkCurrentProcess();
    } else {
      this.ValidateAffectation();
    }
    this.validateReservationCouponStatus(this.pnr);
  }

  callComponentSubcriptions(): void {
    this.subscriptions.add(
      this.layoutService.getReceivedFrom().subscribe((value) => {
        value ? (this.receivedFromHasValue = true) : (this.receivedFromHasValue = false);
        this.isDevelop ? (this.receivedFromHasValue = true) : null;
        this.validateActiveButton();
      })
    );
  }

  setterMenuActionsType() {
    this.settingsService
      .getClientSettings()
      .pipe(
        tap((response) => {
          if (!this.ACTION_TYPES.length) {
            response?.enabledModules?.forEach((module) => {
              this.ACTION_TYPES.push(
                MENU_ACTION_TYPES.find((menu_type) => module === menu_type.id)
              );
            });
            if (this.pnr.itinerary.segments.length <= 0) {
              this.ACTION_TYPES = MENU_ACTION_TYPES.filter(
                (item) => item.id !== 'OpenTicket' && !item.isAvailableAffectation
              );
            }
          }

          if (!this?.pnr?.isVoluntaryChangeAllowed) {
            this.isVoluntaryChangeAllowed = false;
            this.ACTION_TYPES = this.ACTION_TYPES.reduce((prev, curr) => {
              curr.isDisabled = curr.id === 'Voluntary';
              prev.push(curr);
              return prev;
            }, []);
          }
        })
      )
      .subscribe();
  }

  validateReservationCouponStatus(info: PnrInfo) {
    const voluntaryOverrideSegments: number[] = [];
    let voluntaryOverrideSegmentsCounter = -1;
    let voluntaryOverrideSegmentsCalculated = false;
    this.pnrIsEncrypted = info?.isEncrypted ? true : false;

    info.passengers.forEach((passenger) => {
      if (!this.selectedPassengersHasInvalidCoupons) {
        const validSegments: boolean[] = [];
        let openSegmentPassed = false;

        passenger?.ticket?.segments?.forEach((segment) => {
          let currentSegmentIsValid = true;
          let firstOpenPassed = false;

          segment?.flights?.forEach((flight) => {
            if (openSegmentPassed) {
              if (
                flight?.ticketStatus !== TICKET_STATUS.Open &&
                flight?.ticketStatus !== TICKET_STATUS.CTRL &&
                flight?.ticketStatus !== TICKET_STATUS.Control &&
                flight?.ticketStatus !== TICKET_STATUS.AirportControl
              ) {
                currentSegmentIsValid = false;
              }
            } else {
              if (
                flight?.ticketStatus === TICKET_STATUS.Open ||
                flight?.ticketStatus === TICKET_STATUS.CTRL ||
                flight?.ticketStatus === TICKET_STATUS.Control ||
                flight?.ticketStatus === TICKET_STATUS.AirportControl
              ) {
                firstOpenPassed = true;
              } else {
                if (firstOpenPassed) {
                  currentSegmentIsValid = false;
                }
              }
            }
          });

          if (firstOpenPassed) {
            openSegmentPassed = true;
          }

          if (openSegmentPassed && currentSegmentIsValid && !voluntaryOverrideSegmentsCalculated) {
            voluntaryOverrideSegments.push((voluntaryOverrideSegmentsCounter += 1));
          }

          validSegments.push(currentSegmentIsValid);
        });

        if (validSegments.includes(false) || !openSegmentPassed) {
          this.selectedPassengersHasInvalidCoupons = true;
          this.itineraryService.dispatchSetSelectedPassengersHasInvalidCoupons(
            this.selectedPassengersHasInvalidCoupons
          );
        }

        if (voluntaryOverrideSegments.length > 0) {
          voluntaryOverrideSegmentsCalculated = true;
        }
      }
    });

    this.itineraryService.dispatchSetVoluntaryOverrideSegments(voluntaryOverrideSegments);
  }

  ValidateAffectation() {
    if (this.currentProcess$ && this.currentProcess$ === ServicesIntegration.involuntaryChange) {
      let actualAffectation = false;
      this.pnr.itinerary.segments.forEach((segment) => {
        actualAffectation = segment?.recommendationFlights?.length > 0;
        if (!this.existAffectation && actualAffectation) {
          this.existAffectation = true;
          this.checkCurrentProcess();
        }
      });

      if (!this.existAffectation) {
        this.actionType = OptionType.ExchangeNoCost;
        this.changeAction(false);
        this.currentProcess$ = null;
        this.isSalesForce = true;
      }
    } else {
      this.existAffectation = true;
      this.checkCurrentProcess();
    }
  }

  ValidateIsAirline() {
    this.settingsService.getClientSettings().subscribe((settings) => {
      this.isAirline = settings.isAirline;
    });
  }

  buildForm() {
    this.actionForm = this.fb.group({
      actionType: [this.currentProcess$ ? this.currentProcess$ : '', Validators.required]
    });
  }

  checkCurrentProcess() {
    switch (this.currentProcess$) {
      case ServicesIntegration.voluntaryChange:
        this.actionType = OptionType.VoluntaryChange;
        this.changeAction(false);
        break;

      case ServicesIntegration.involuntaryChange:
        this.actionType = OptionType.ExchangeNoCost;
        this.changeAction(false);
        break;
    }
  }

  changeAction(useFormValue: boolean) {
    useFormValue ? (this.actionType = this.actionForm.controls.actionType.value) : null;

    if (this.actionType === OptionType.VoluntaryChange) {
      this.actionType = this.isVoluntaryChangeAllowed ? OptionType.VoluntaryChange : null;
    }
    this.changeActiveAction.emit(this.actionType);
    this.tooltipMessage = '';

    if (this.isSelectedInvoluntary) {
      this.isSelectedInvoluntary = false;
      return;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedPassengersQuote || changes.selectedSegments) {
      changes.selectedPassengersQuote?.currentValue &&
      changes.selectedPassengersQuote.currentValue.length > 0
        ? (this.reRouteDisabled = false)
        : (this.reRouteDisabled = true);
    }
    this.validateActiveButton();
  }

  validateActiveButton() {
    if (this.activeButtonCrucialValidations()) {
      this.buttonDisabled = true;
    } else {
      if (this.actionType !== OptionType.InvoluntaryChange && this.canContinue()) {
        this.buttonDisabled = false;
      } else {
        this.continueButtonValidations();
      }
    }
  }

  activeButtonCrucialValidations(): boolean {
    return (
      this.selectedPassengersHasInvalidCoupons ||
      this.validateWarnings() ||
      this.pnrIsEncrypted ||
      !this.receivedFromHasValue
    );
  }

  continueButtonValidations() {
    if (this.actionType === OptionType.InvoluntaryChange && this.isInvoluntaryChange()) {
      this.buttonDisabled = false;
    } else {
      this.continueButtonValidations2();
    }
  }

  continueButtonValidations2() {
    if (
      (this.actionType === OptionType.VoluntaryChange ||
        this.actionType === OptionType.ExchangeNoCost) &&
      (this.ticketOpen || this.noItinerary) &&
      this.hasPassengers()
    ) {
      this.buttonDisabled = false;
    } else {
      this.continueButtonValidations3();
    }
  }

  continueButtonValidations3() {
    if (
      this.actionType === OptionType.OpenTicket &&
      (this.noItinerary || this.ticketOpen) &&
      this.hasPassengers()
    ) {
      this.buttonDisabled = false;
    } else if (this.actionType === OptionType.Cancellation && this.hasPassengers()) {
      this.buttonDisabled = false;
    } else if (this.exceptionMode && this.hasPassengers()) {
      this.buttonDisabled = false;
    } else {
      this.buttonDisabled = true;
    }
  }

  validateWarnings() {
    if (this.pnr?.warnings.length) {
      let PNR_MODIFIED = this.pnr.warnings.find((warning) =>
        warning.includes(WARNING_TEXT.PNR_MODIFIED)
      );
      return !!PNR_MODIFIED;
    } else {
      return false;
    }
  }

  canContinue(): boolean {
    return this.actionType && this.hasSegments() && this.hasPassengers();
  }

  isInvoluntaryChange(): boolean {
    return this.hasPendingChanges && this.hasPassengers();
  }

  hasPassengers(): boolean {
    return !!this.selectedPassengersQuote && this.selectedPassengersQuote.length > 0;
  }

  hasSegments(): boolean {
    return !!this.selectedSegments && this.selectedSegments.length > 0;
  }

  loadAlternativesRequest() {
    if (!this.hasPassengers()) {
      this.selectedPassengersQuote = [];
    }
    this.actionType = OptionType.ExchangeNoCost;
    this.changeActiveAction.emit(this.actionType);
    this.validateSendQuoteRequest();
  }

  checkAlertMessages() {
    if (
      this.actionType === OptionType.ExchangeNoCost &&
      this.isSalesForce &&
      !this.existAffectation &&
      !this.exceptionMode
    ) {
      this.isSelectedInvoluntary = true;

      return;
    }
    if (
      this.actionType === OptionType.VoluntaryChange &&
      this.isAirline &&
      !this.pnr.canHaveVoluntaryChange
    ) {
      this.showCanVoluntaryChange = true;
      return;
    }

    this.layoutService.getAlertMessages().subscribe((res) => {
      if (res.length > 0) {
        this.alertMessages = res;
        let anyMessageToShow = false;
        res.forEach((message) => {
          if (!message.isAccepted) {
            anyMessageToShow = true;
          }
        });
        this.showAlertsModal = anyMessageToShow;
      } else {
        this.allMessagesAcepted();
      }
    });
  }

  closeAlertsModal() {
    this.showAlertsModal = false;
  }

  allMessagesAcepted() {
    if (this.exceptionMode) {
      this.isModalOpen = !this.isModalOpen;
    } else {
      this.optionRequest.emit(this.buildQuoteRequest());
    }
    this.closeAlertsModal();
  }

  validateSendQuoteRequest() {
    this.checkAlertMessages();
  }

  buildQuoteRequest(
    exceptionData?: { isException: boolean; processType: string } | undefined
  ): QuoteRequest {
    let pnrOption = '';
    const logActionType = this.reportLogActionTypeMap[this.actionType];

    if (typeof logActionType !== 'undefined') {
      this.reportLog.Start(logActionType);
    }
    if (exceptionData?.isException) {
      switch (exceptionData?.processType) {
        case ExceptionProcessType.Contingency:
          pnrOption = OptionType.InvoluntaryChange;
          this.actionType = OptionType.ExchangeNoCost;
          break;

        case ExceptionProcessType.Exception:
          pnrOption = OptionType.VoluntaryChange;
          this.actionType = OptionType.VoluntaryChange;
          break;

        default:
          break;
      }
    } else {
      pnrOption =
        this.actionType === OptionType.ExchangeNoCost
          ? OptionType.InvoluntaryChange
          : this.actionType;
    }

    return {
      pnrOption,
      flights: this.selectedSegments,
      passengers: this.selectedPassengersQuote,
      isException: this.exceptionMode,
      ...this.waiver
    };
  }

  onSubmitExceptionForm(waiverData: WaiverData) {
    this.waiver = waiverData;
    this.optionRequest.emit(
      this.buildQuoteRequest({ isException: true, processType: waiverData.processType })
    );
    this.isModalOpen = !this.isModalOpen;
  }

  reRouteModalQuote() {
    this.showReRouteModal = true;
  }

  exit() {
    this.showCanVoluntaryChange = false;
  }

  closeReRouteModal() {
    this.showReRouteModal = false;
  }

  saveReRouteModalData(modalQuote: IReRouteModalQuote) {
    this.showReRouteModal = false;
    this.reRouteService.dispatchSaveModalQuote(modalQuote);
    this.router.navigate(['/re-route']);
  }

  goToDashboard() {
    this.reportLog.FinishByUser(ActionTypeText.NA);
    this.itineraryService.goToDashboard();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
